import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import BigNumber from 'bignumber.js';
import { OrderCardContext } from '../../../../../contexts/card/cardOrder/context';
import { useLedgerAccounts } from '../../../../../utilities/sharedHooks/ledgerSDKHooks';
import {
  RATES_QUERY_KEY,
  SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  USER_PREFERENCES,
} from '../../../../../utilities/sharedQueries';
import { currencies, exchange, walletTypes } from '../../../../../utilities/constants';
import { formatNumber, formatNumberWithComma } from '../../../../../utilities/formatHelpers';
import { CardPaymentMethods, orderCardFlowScreens } from '../../../constants';
import { useAccountRestriction } from '../../../../../utilities/sharedHooks/account';
import ModalBodyLayout from '../partials/MainBodyLayout';
import AddAccount from '../../../../Accounts/partials/AddAccount';
import { useLedgerFilteredCurrencies } from '../../../../../utilities/sharedHooks/ledgerWallets';
import { showAlert } from '../../../../../utilities/notifications';

const SelectPayment = () => {
  const { t: translate } = useTranslation();
  const { state, openScreen, updatePaymentInfo } = useContext(OrderCardContext);

  const userWallets = state?.userWallets;
  const isUS = state?.user?.isUS;
  const featureAllowCardOrderPaymentStablecoin =
    state?.cardOrderSettings?.app?.feature_allow_card_order_payment_stablecoin;
  const featureAllowCardOrderPaymentCrypto =
    state?.cardOrderSettings?.app?.feature_allow_card_order_payment_crypto;

  const screenOptions = state?.screenOptions;
  const cardOrderFee = state?.cardOrderFee;

  const [searchValue, setSearchValue] = useState('');
  const [filteredCoins, setFilteredCoins] = useState([]);
  const [ledgerAccounts] = useLedgerAccounts();
  const { data: rates } = useQuery(RATES_QUERY_KEY);
  const { restrictions } = useAccountRestriction();

  const { data: supportedCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);

  const { data: userPref } = useQuery(USER_PREFERENCES);
  const fiatCurrency = userPref?.displayCurrency;

  const allowCryptoCurrencies =
    state?.payment?.method === CardPaymentMethods.CRYPTO_OR_STABLE &&
    featureAllowCardOrderPaymentCrypto;

  const allowStablecoinCurrencies =
    state?.payment?.method === CardPaymentMethods.CRYPTO_OR_STABLE &&
    featureAllowCardOrderPaymentStablecoin;

  const accountType =
    state?.payment?.method === CardPaymentMethods.CRYPTO_OR_STABLE
      ? walletTypes.CRYPTO
      : state?.payment?.method;

  useEffect(() => {
    if (searchValue) {
      const availableWallets = userWallets.filter(
        (wallet) =>
          wallet.coin?.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1 ||
          (supportedCurrencies[wallet.coin]?.currency_name &&
            supportedCurrencies[wallet.coin]?.currency_name
              ?.toLowerCase()
              ?.indexOf(searchValue.toLowerCase()) !== -1) ||
          (supportedCurrencies[wallet.coin]?.currency_display_name &&
            supportedCurrencies[wallet.coin]?.currency_display_name
              ?.toLowerCase()
              ?.indexOf(searchValue.toLowerCase()) !== -1)
      );

      const availableCoin = availableWallets.map((w) => ({
        id: w.id,
        coin: w.coin.toUpperCase(),
      }));
      setFilteredCoins(availableCoin);
    } else {
      const availableCoin = userWallets.map((w) => ({
        id: w.id,
        coin: w.coin.toUpperCase(),
      }));
      setFilteredCoins(availableCoin);
    }
  }, [searchValue, userWallets, supportedCurrencies]);

  const getAvailableBalanceForCurrency = (coin) => {
    const currentWallet = userWallets.find((w) => w.coin === coin.toLowerCase());
    const currentCurrency = currencies[coin?.toUpperCase()];
    const walletBalance = formatNumber(
      currentWallet?.available_balance * currentCurrency?.conversion,
      currentWallet?.decimal
    );

    const _source = coin?.toUpperCase();
    const _destination = fiatCurrency?.toUpperCase();

    let _rate = 0;
    if (rates) {
      if (_source === _destination) {
        _rate = 1;
      } else if (rates[`${_source}${_destination}`]) {
        _rate = new BigNumber(rates[`${_source}${_destination}`].ask);
      } else if (rates[`${_destination}${_source}`]) {
        _rate = new BigNumber(1).dividedBy(rates[`${_destination}${_source}`].ask);
      }
    }

    const fiatBalance = formatNumberWithComma(
      formatNumber(_rate * walletBalance, exchange.FIAT_VALUE)
    );
    return `${fiatBalance} ${fiatCurrency?.toUpperCase()}`;
  };

  const handleSearchInput = (event) => {
    setSearchValue(event.target.value);
  };

  const getCardOrderFees = () => {
    const cardFee = formatNumber(
      cardOrderFee ? cardOrderFee / 100 : 0,
      currencies[state.cardCurrency.coin.toLowerCase()].decimal
    );

    return cardFee;
  };

  const getChargedTotalInCurrency = (onlyAmount = false, coin) => {
    const cardFees = getCardOrderFees();
    const { ask } = rates[`${coin.toUpperCase()}${state.cardCurrency.coin.toUpperCase()}`] || {};
    if (ask) {
      const rate = new BigNumber(1).dividedBy(ask);

      if (rates) {
        const fiatBalance = formatNumberWithComma(
          formatNumber(rate * cardFees, exchange.CRYPTO_VALUE)
        );
        return onlyAmount ? fiatBalance : `${fiatBalance} ${state.payment.currency}`;
      }
    }
    const totalFiat = formatNumberWithComma(formatNumber(cardFees, exchange.FIAT_VALUE));
    return onlyAmount ? totalFiat : `${totalFiat} ${state.cardCurrency.coin.toUpperCase()}`;
  };

  const checkIsSufficientBalance = (currentWallet, coin) => {
    const cardFee = getChargedTotalInCurrency(true, coin);
    const currentCurrency = currencies[coin.toUpperCase()];
    const currentBalance = formatNumber(
      currentWallet?.available_balance * currentCurrency?.conversion,
      currentWallet?.decimal
    );
    return BigNumber(currentBalance).gte(cardFee);
  };

  const notAvailableToMoveToReceiveFlow = (coin) => {
    const currentWallet = userWallets.find((w) => w.coin === coin.toLowerCase());

    const notValidBalance =
      currentWallet.available_balance === 0 ||
      currentWallet.available_balance === '0' ||
      !checkIsSufficientBalance(currentWallet, coin);

    return (
      restrictions[coin?.toLowerCase()]?.receiveDisabled &&
      (screenOptions?.isTopUp || notValidBalance)
    );
  };

  const handlePayment = (coin) => {
    const currentWallet = userWallets.find((w) => w.coin === coin.toLowerCase());
    updatePaymentInfo('currency', coin);
    if (
      currentWallet.available_balance === 0 ||
      currentWallet.available_balance === '0' ||
      !checkIsSufficientBalance(currentWallet, coin)
    ) {
      const isDepositHidden =
        process.env.REACT_APP_DEPOSIT_BUTTON_HIDDEN &&
        !isUS &&
        !['usdc', 'euroc'].includes(coin?.toLowerCase());

      if (!isDepositHidden) {
        openScreen(orderCardFlowScreens.LEDGER_DEPOSIT, {
          wallet: currentWallet,
          ledgerAccount: ledgerAccounts[coin.toLowerCase()],
        });
      } else {
        showAlert({ message: translate('error_no_enough_available_funds'), type: 'danger' });
      }
    } else {
      openScreen(orderCardFlowScreens.CONFORM_PAYMENT_DETAILS, { ...screenOptions });
    }
  };

  const onPrevClick = () => {
    if (screenOptions?.isTopUp) {
      openScreen(orderCardFlowScreens.FREE_CARD_PROMO);
    } else {
      openScreen(orderCardFlowScreens.SELECT_PAYMENT_TYPE);
    }
  };

  return (
    <ModalBodyLayout
      withBack
      onPrevClick={onPrevClick}
      title={translate('card_order_select_payment_title_type').replace(
        '_accountType_',
        accountType
      )}>
      <div className="mt-4 px-3">
        <div className="rounded bg-light p-0 popup-search-input d-flex flex-row flex-row-reverse position-relative mb-4">
          <input
            type="text"
            value={searchValue}
            onChange={(e) => handleSearchInput(e)}
            className="form-control border-0 bg-lighter shadow-none w-100 ps-5 d-flex"
            placeholder={translate('select_account_input_placeholder')}
          />
          <i className="position-absolute start-0 top-0 px-3 py-3 icon icon-search-other h-100 d-flex align-items-center justify-content-center text-shadow" />
          <i
            onClick={() => {
              setSearchValue('');
            }}
            onKeyDown={() => {}}
            role="button"
            tabIndex={0}
            label="close"
            hidden={searchValue === '' || searchValue === null || searchValue === undefined}
            className="icon icon-close-other text-white position-absolute top-50 end-0 translate-middle-y pe-3"
          />
        </div>
        {filteredCoins?.map((key) => {
          const currency =
            supportedCurrencies && key?.coin && supportedCurrencies[key?.coin?.toLowerCase()]
              ? supportedCurrencies[key?.coin?.toLowerCase()]
              : {};

          const allowedCurrency =
            currency &&
            ((allowCryptoCurrencies && currency?.currency_type === walletTypes.CRYPTO) ||
              (allowStablecoinCurrencies && currency?.currency_type === walletTypes.STABLE) ||
              currency?.currency_type === state.paymentMethodType);

          if (allowedCurrency) {
            return (
              <div key={`${currency.currency_name}-${key?.id}`}>
                <AddAccount
                  label={currency.currency_name}
                  icon={key?.coin}
                  subLabel={getAvailableBalanceForCurrency(currency.currency_code)}
                  isImg
                  disabled={notAvailableToMoveToReceiveFlow(key?.coin?.toLowerCase())}
                  noMobile
                  imageStyle={{ width: '2rem', height: '2rem' }}
                  className="my-2 bg-lighter"
                  onClick={() => {
                    handlePayment(key?.coin);
                  }}
                />
              </div>
            );
          }
          return '';
        })}
      </div>
    </ModalBodyLayout>
  );
};

export default SelectPayment;
